import React from "react";
import PDVlogo from "../../experience/landingpage/header/assets/PDVlogo.svg";
import "./invoice.css";

export const formatInvoiceMoney = (value) =>
  typeof value === "number" && !Number.isNaN(value)
    ? `$${value.toFixed(2)}`
    : "—";

function formatCellMoney(value) {
  if (value == null || value === "") return "—";
  if (typeof value === "number") return formatInvoiceMoney(value);
  return String(value);
}

/** Right column: currency for totals/prices; plain number for quantity-only rows (e.g. user count). */
function formatBillingRightCell(row) {
  if (typeof row.total === "number") return formatCellMoney(row.total);
  if (typeof row.unitPrice === "number") return formatCellMoney(row.unitPrice);
  if (row.quantity != null && row.quantity !== "") return String(row.quantity);
  return "—";
}

function Invoice({ data }) {
  if (!data) {
    return null;
  }
  const d = data;
  const summary = d.accountSummary ?? {};
  const rows = Array.isArray(d.billingRows) ? d.billingRows : [];

  return (
    <div className="inv-shell">
      <article className="inv-card">
        <header className="inv-header">
          <div className="inv-brand">
            <img src={PDVlogo} alt="" width={120} height={80} />
          </div>
          <div className="inv-org">
            <p className="inv-org-title">Hospice Volunteer Association</p>
            <p className="inv-org-sub">Patient Data Vault Services</p>
          </div>
        </header>

        <div className="inv-account">
          <div className="inv-account-left">
            <p className="inv-account-label">Account Number</p>
            <p className="inv-account-value">{d.accountNumber}</p>
          </div>

          <div>
            <p className="inv-address font-weight-bold">
              {d?.organizationName}
            </p>
            {d?.address ? <p className="inv-address">{d?.address}</p> : null}
          </div>
        </div>

        {d?.codingNumber && (
          <div className="inv-account">
            <div>
              <p className="inv-address font-weight-bold">{d?.codingNumber}</p>
            </div>
          </div>
        )}

        <h1 className="inv-doc-title">INVOICE</h1>

        <div className="inv-meta-grid">
          <div className="inv-meta-item">
            <span className="inv-meta-label">Invoice Number</span>
            <span className="inv-meta-value">{d.invoiceNumber ?? "—"}</span>
          </div>
          <div className="inv-meta-item">
            <span className="inv-meta-label">Invoice date</span>
            <span className="inv-meta-value">{d.invoiceDate ?? "—"}</span>
          </div>
          <div className="inv-meta-item">
            <span className="inv-meta-label">Due date</span>
            <span className="inv-meta-value">{d.dueDate ?? "—"}</span>
          </div>
          <div className="inv-meta-item">
            <span className="inv-meta-label">Amount due</span>
            <span className="inv-meta-value inv-meta-value--highlight">
              {formatInvoiceMoney(d.amountDue)}
            </span>
          </div>
        </div>

        <section className="inv-section" aria-labelledby="inv-summary-heading">
          <h2 id="inv-summary-heading" className="inv-section-head">
            Account summary
          </h2>
          <div className="inv-rows">
            <div className="inv-row">
              <span className="inv-row-label">
                Current Charges For Services
              </span>
              <span className="inv-row-value">
                {formatInvoiceMoney(summary.currentCharges)}
              </span>
            </div>
            <div className="inv-row">
              <span className="inv-row-label">Previous Account Balance</span>
              <span className="inv-row-value">
                {formatInvoiceMoney(summary.previousBalance)}
              </span>
            </div>
            {summary.HVASubscriptionAmount && (
              <div className="inv-row">
                <span className="inv-row-label">HVA Membership</span>
                <span className="inv-row-value">
                  {formatInvoiceMoney(summary.HVASubscriptionAmount)}
                </span>
              </div>
            )}
            <div className="inv-row inv-row--total">
              <span className="inv-row-label">
                Total Amount Due
                {summary.payByDate ? ` by ${summary.payByDate}` : ""}
              </span>
              <span className="inv-row-value">
                {formatInvoiceMoney(summary.totalDue)}
              </span>
            </div>
          </div>
        </section>

        <section className="inv-section" aria-labelledby="inv-billing-heading">
          <h2 id="inv-billing-heading" className="inv-section-head">
            Billing details
          </h2>
          {d.billingPeriod ? (
            <p className="inv-section-sub">Billing Period: {d.billingPeriod}</p>
          ) : null}
          <p className="inv-billing-heading">
            Volunteer Report Management Service
          </p>

          <div className="inv-table-wrap">
            <table className="inv-table">
              <tbody>
                {rows.map((row, i) => (
                  <tr key={row.id ?? i}>
                    <td>{row.serviceName ?? "—"}</td>
                    <td className="inv-td-num">
                      {formatBillingRightCell(row)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>Total Monthly Service Fees</td>
                  <td className="inv-td-num">
                    {formatInvoiceMoney(d.billingSubtotal)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {summary.HVASubscriptionAmount && (
            <>
              <p className="inv-billing-heading mt-3">
                HVA Membership Dues (Expires: {d.HVASubscriptionExpiryDate})
              </p>
              <div className="inv-table-wrap">
                <table className="inv-table">
                  <tbody>
                    <tr>
                      <td>Annual Membership Fee Renewal</td>
                      <td className="inv-td-num">
                        {formatInvoiceMoney(summary.HVASubscriptionAmount)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>

        {summary.HVASubscriptionAmount && (
          <div
            className="inv-row"
            style={{
              fontSize: "1rem",
              fontWeight: 700,
              color: "var(--primary-text-color)",
              border: "none"
            }}
          >
            Total for Services and Other Charges{" "}
            <span
              className="inv-row-value"
              style={{
                color: "#0593a1",
                fontSize: "1.125rem",
                fontWeight: 700,
              }}
            >
              {formatInvoiceMoney(summary.totalDue)}
            </span>
          </div>
        )}

        <footer className="inv-footer">
          <p className="inv-footer-support">
            "Questions? Contact PDV Services through the PDV Help Desk."
          </p>
        </footer>
      </article>
    </div>
  );
}

export default Invoice;
